import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';
import type { Theme, ThemeOptions } from '@mui/material/styles';
import { createTheme, styled, ThemeProvider, useTheme } from '@mui/material/styles';
import type { TypographyOptions } from '@mui/material/styles/createTypography';
import { createDarkPrismNavigationPalette } from './prismDarkNavigationPalette';
import {
  PRISM_NAVIGATION_PALETTE,
  PRISM_PRIMITIVE_PALETTE,
  PRISM_RADIUS,
  PRISM_SEMANTIC_COLORS,
  PRISM_TYPE,
} from './prismDesignTokens';

const px = (value: number) => `${value}px`;

type PrismTypeSize = keyof typeof PRISM_TYPE.size;
type PrismTypeWeight = keyof typeof PRISM_TYPE.weight;

const createPrismTextStyle = (size: PrismTypeSize, weight: PrismTypeWeight) => ({
  fontFamily: PRISM_TYPE.family.main,
  fontSize: px(PRISM_TYPE.size[size]),
  fontWeight: PRISM_TYPE.weight[weight],
  letterSpacing: PRISM_TYPE.letterSpacing.none,
  lineHeight: px(PRISM_TYPE.lineHeight[size]),
});

const createPrismTypography = (): TypographyOptions => ({
  fontFamily: PRISM_TYPE.family.main,
  fontSize: PRISM_TYPE.size.m,
  fontWeightLight: PRISM_TYPE.weight.light,
  fontWeightRegular: PRISM_TYPE.weight.regular,
  fontWeightMedium: PRISM_TYPE.weight.semiBold,
  fontWeightBold: PRISM_TYPE.weight.bold,
  allVariants: {
    fontFamily: PRISM_TYPE.family.main,
  },
  h1: createPrismTextStyle('5xl', 'regular'),
  h2: createPrismTextStyle('4xl', 'regular'),
  h3: createPrismTextStyle('3xl', 'semiBold'),
  h4: createPrismTextStyle('2xl', 'semiBold'),
  h5: createPrismTextStyle('xl', 'semiBold'),
  h6: createPrismTextStyle('l', 'semiBold'),
  subtitle1: createPrismTextStyle('l', 'regular'),
  subtitle2: createPrismTextStyle('m', 'regular'),
  body1: createPrismTextStyle('l', 'regular'),
  body2: createPrismTextStyle('m', 'regular'),
  button: {
    ...createPrismTextStyle('m', 'semiBold'),
    textTransform: 'none',
  },
  caption: createPrismTextStyle('s', 'regular'),
  overline: {
    ...createPrismTextStyle('xs', 'semiBold'),
    textTransform: 'uppercase',
  },
}) as TypographyOptions;

// Prism is a fixed light design system; under Workboard's dark theme, swap the chrome-bearing
// tokens for the ambient dark theme instead (see prismDarkNavigationPalette.ts).
const createPrismPalette = (baseTheme: Theme): ThemeOptions['palette'] => {
  const isDark = baseTheme.palette.mode === 'dark';

  return {
    ...PRISM_PRIMITIVE_PALETTE,
    common: {
      black: PRISM_PRIMITIVE_PALETTE.neutral.black,
      white: PRISM_PRIMITIVE_PALETTE.neutral.white,
    },
    navigation: isDark ? createDarkPrismNavigationPalette(baseTheme) : PRISM_NAVIGATION_PALETTE,
    primary: isDark
      ? {
        main: baseTheme.palette.primary.main,
        light: baseTheme.palette.primary.light,
        dark: baseTheme.palette.primary.dark,
        contrastText: baseTheme.palette.primary.contrastText,
      }
      : {
        main: PRISM_SEMANTIC_COLORS.primary.main,
        light: PRISM_SEMANTIC_COLORS.primary.hover,
        dark: PRISM_SEMANTIC_COLORS.primary.pressed,
        contrastText: PRISM_SEMANTIC_COLORS.primary.contrast,
      },
    text: isDark
      ? {
        link: baseTheme.palette.primary.light,
        primary: baseTheme.palette.text.primary,
        secondary: baseTheme.palette.text.secondary,
        disabled: baseTheme.palette.text.disabled,
      }
      : {
        link: PRISM_SEMANTIC_COLORS.text.link,
        primary: PRISM_SEMANTIC_COLORS.text.primary,
        secondary: PRISM_SEMANTIC_COLORS.text.secondary,
        disabled: PRISM_SEMANTIC_COLORS.text.disabled,
      },
    background: isDark
      ? { default: baseTheme.palette.background.default, paper: baseTheme.palette.background.paper }
      : { default: PRISM_SEMANTIC_COLORS.background.page, paper: PRISM_SEMANTIC_COLORS.background.paper },
    action: isDark
      ? {
        disabled: baseTheme.palette.action.disabled,
        disabledBackground: baseTheme.palette.action.disabledBackground,
        focus: baseTheme.palette.action.focus,
      }
      : {
        disabled: PRISM_SEMANTIC_COLORS.action.disabled,
        disabledBackground: PRISM_SEMANTIC_COLORS.action.disabledBackground,
        focus: PRISM_SEMANTIC_COLORS.action.focus,
      },
    divider: isDark ? baseTheme.palette.divider : PRISM_SEMANTIC_COLORS.stroke.subtle,
    error: {
      main: PRISM_SEMANTIC_COLORS.feedback.error,
      contrastText: PRISM_SEMANTIC_COLORS.primary.contrast,
    },
    success: {
      main: PRISM_SEMANTIC_COLORS.feedback.success,
      light: PRISM_SEMANTIC_COLORS.feedback.successHover,
      dark: PRISM_SEMANTIC_COLORS.feedback.successPressed,
      contrastText: PRISM_SEMANTIC_COLORS.primary.contrast,
    },
    warning: {
      main: PRISM_SEMANTIC_COLORS.feedback.warning,
      contrastText: isDark ? baseTheme.palette.text.primary : PRISM_SEMANTIC_COLORS.text.primary,
    },
    info: {
      main: PRISM_SEMANTIC_COLORS.feedback.info,
      contrastText: PRISM_SEMANTIC_COLORS.primary.contrast,
    },
  };
};

// Reuse the derived Prism theme for each parent MUI theme instance. This keeps
// nested Prism providers cheap across Auth, PrimaryNavigation, and SecondaryNavigation.
const prismThemeCache = new WeakMap<Theme, Theme>();

const getPrismTheme = (baseTheme: Theme) => {
  const cachedTheme = prismThemeCache.get(baseTheme);
  if (cachedTheme) {
    return cachedTheme;
  }

  const prismThemeOptions: ThemeOptions = {
    palette: createPrismPalette(baseTheme),
    shape: {
      borderRadius: PRISM_RADIUS.xs,
      radius: PRISM_RADIUS,
    },
    typography: createPrismTypography(),
  };

  const prismTheme = createTheme(baseTheme, prismThemeOptions);
  prismThemeCache.set(baseTheme, prismTheme);
  return prismTheme;
};

// MUI components read typography from ThemeProvider. Plain DOM nodes still need
// an inheritable CSS boundary; `display: contents` avoids adding layout boxes.
const PrismThemeScope = styled('div')(({ theme }) => ({
  color: theme.palette.text.primary,
  display: 'contents',
  fontFamily: theme.typography.fontFamily,
}));

const PrismThemeProvider = ({ children }: PropsWithChildren) => {
  const theme = useTheme();
  const prismTheme = useMemo(() => getPrismTheme(theme), [theme]);

  return (
    <ThemeProvider theme={prismTheme}>
      <PrismThemeScope>{children}</PrismThemeScope>
    </ThemeProvider>
  );
};

export default PrismThemeProvider;
